import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Timer, Send } from "lucide-react";

export default function TestPlayer() {
  const { testId } = useParams();
  const [test, setTest] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(0);
  const started = useRef(Date.now());
  const nav = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    api.get(`/tests/${testId}`)
      .then(r => {
        setTest(r.data);
        setRemaining(r.data.duration_minutes * 60);
      })
      .catch(e => {
        toast.error(formatApiError(e.response?.data?.detail));
        nav("/dashboard");
      });
  }, [testId, nav]);

  useEffect(() => {
    if (!test || remaining <= 0) return;
    const id = setInterval(() => setRemaining(r => r - 1), 1000);
    return () => clearInterval(id);
  }, [test, remaining]);

  useEffect(() => { if (test && remaining === 0) submit(); /* eslint-disable-next-line */ }, [remaining]);

  const submit = async () => {
    const dur = Math.round((Date.now() - started.current) / 1000);
    const payload = {
      test_id: test.id, duration_seconds: dur,
      answers: test.questions.map(q => ({ question_id: q.id, answer: answers[q.id] || null })),
    };
    try {
      const { data } = await api.post("/tests/submit", payload);
      sessionStorage.setItem("last-result", JSON.stringify(data));
      nav("/tests/result");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!test) return <Layout><div className="max-w-5xl mx-auto p-8">Caricamento…</div></Layout>;

  const q = test.questions[idx];
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const answered = Object.keys(answers).length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        {/* Header bar */}
        <div className="border border-slate-200 bg-white flex items-center justify-between p-4 mb-6">
          <div>
            <div className="label-mono text-slate-500">{test.title}</div>
            <div className="font-display font-bold text-lg">{t("q_of")} {idx + 1} {t("of")} {test.questions.length}</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="label-mono text-slate-500">Risposte: <span className="font-mono-num text-slate-900">{answered}/{test.questions.length}</span></div>
            <div className={`flex items-center gap-2 px-4 py-2 border ${remaining < 60 ? "border-red-500 text-red-600 tick" : "border-slate-300"}`}>
              <Timer className="w-4 h-4" />
              <span className="font-mono-num text-lg font-bold">{mm}:{ss}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question column */}
          <div className="lg:col-span-3 border border-slate-200 bg-white p-8">
            <div className="label-mono text-[#002FA7] mb-4">{q.kind === "verbal" ? "VERBALE" : q.kind === "abstract-visual" ? "ASTRATTO" : q.kind === "numerical" ? "NUMERICO" : "MISTO"}</div>
            <p className="text-[15px] leading-relaxed whitespace-pre-line mb-6 text-slate-800">{q.prompt}</p>

            {/* Numerical table */}
            {q.table && (
              <div className="border border-slate-300 mb-8 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 label-mono">
                    <tr>{q.table.headers.map((h, i) => (
                      <th key={i} className="text-left p-3 border-r last:border-r-0 border-slate-300">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {q.table.rows.map((row, ri) => (
                      <tr key={ri} className="border-t border-slate-300">
                        {row.map((cell, ci) => (
                          <td key={ci} className={`p-3 border-r last:border-r-0 border-slate-200 ${ci > 0 ? "font-mono-num" : "font-medium"}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Abstract-visual 3x3 matrix */}
            {q.matrix && (
              <div className="mb-8 flex justify-center">
                <div className="grid grid-cols-3 border border-slate-400" style={{ width: 360 }}>
                  {q.matrix.map((cell, i) => (
                    <div key={i} className={`aspect-square border border-slate-300 ${cell ? "bg-white" : "bg-slate-50 flex items-center justify-center"}`}>
                      {cell
                        ? <div className="w-full h-full p-2" dangerouslySetInnerHTML={{ __html: cell }} />
                        : <span className="text-3xl font-black text-[#002FA7]">?</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Options */}
            <div className={q.matrix ? "grid grid-cols-2 md:grid-cols-4 gap-3" : "space-y-3"}>
              {Object.entries(q.options).map(([k, v]) => {
                const selected = answers[q.id] === k;
                const isSvg = typeof v === "string" && v.trim().startsWith("<svg");
                if (isSvg) {
                  return (
                    <label key={k} data-testid={`opt-${q.id}-${k}`}
                      className={`border cursor-pointer transition-all p-3 flex flex-col items-center ${selected ? "border-[#002FA7] bg-[#002FA7]/5 shadow-hard" : "border-slate-200 hover:border-slate-400"}`}>
                      <input type="radio" name={q.id} className="sr-only"
                        checked={selected} onChange={() => setAnswers({ ...answers, [q.id]: k })} />
                      <span className="label-mono text-[#002FA7] mb-2">{k}</span>
                      <div className="w-20 h-20" dangerouslySetInnerHTML={{ __html: v }} />
                    </label>
                  );
                }
                return (
                  <label key={k} data-testid={`opt-${q.id}-${k}`}
                    className={`flex gap-4 p-4 border cursor-pointer transition-colors ${selected ? "border-[#002FA7] bg-[#002FA7]/5" : "border-slate-200 hover:border-slate-400"}`}>
                    <input type="radio" name={q.id} className="mt-1 accent-[#002FA7]"
                      checked={selected} onChange={() => setAnswers({ ...answers, [q.id]: k })} />
                    <div>
                      <span className="label-mono mr-3 text-[#002FA7]">{k}</span>
                      <span>{v}</span>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button variant="outline" className="rounded-none label-mono" data-testid="prev-q"
                disabled={idx === 0} onClick={() => setIdx(idx - 1)}>{t("prev")}</Button>
              {idx < test.questions.length - 1 ? (
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono" data-testid="next-q"
                  onClick={() => setIdx(idx + 1)}>{t("next")}</Button>
              ) : (
                <Button className="rounded-none bg-[#FFD700] hover:bg-yellow-300 text-[#0A0F1A] label-mono" data-testid="submit-test-btn" onClick={submit}>
                  <Send className="w-4 h-4 mr-2" />{t("submit_test")}
                </Button>
              )}
            </div>
          </div>

          {/* Grid of questions */}
          <div className="lg:col-span-1 border border-slate-200 bg-white p-4 h-fit sticky top-20">
            <div className="label-mono text-slate-500 mb-3">Domande</div>
            <div className="grid grid-cols-5 gap-2">
              {test.questions.map((qq, i) => {
                const ans = !!answers[qq.id];
                const cur = i === idx;
                return (
                  <button key={qq.id} onClick={() => setIdx(i)} data-testid={`goto-q-${i}`}
                    className={`aspect-square text-sm font-mono-num border ${cur ? "border-[#002FA7] bg-[#002FA7] text-white" : ans ? "border-[#002FA7] text-[#002FA7]" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <Button className="w-full mt-4 rounded-none bg-[#0A0F1A] hover:bg-slate-800 label-mono" data-testid="submit-side" onClick={submit}>
              Termina e correggi
            </Button>
            <Link to="/dashboard" className="block text-center text-xs text-slate-500 mt-3 hover:underline">← esci</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
